import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { BrandingService } from '@next/core-lib/branding';
import { PageToolbarComponent, PrintPageComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { AgrDatePipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteSelectors, SiteState } from '@next/core-lib/site';
import { Customer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { BrandingLogoSrc } from '../branding/branding.model';
import { BenchmarkSettingsComponent } from './components/benchmark-settings/benchmark-settings.component';
import { DataFilterComponent } from './components/data-filter/data-filter.component';
import { DataViewComponent } from './components/data-view/data-view.component';
import { BenchmarkSettings, QueryableFlockData, ViewMode } from './models/farmer-insights.model';
import { setFarmerInsightsBenchmarkSettings } from './store/actions/farmer-insights.actions';
import {
  getBenchmarkSettings,
  getIsDetailedModeSelected,
  getQueryableFlockData,
  getSelectedViewMode,
} from './store/selectors/farmer-insights.selectors';

// Local type definition - Orientation is not exported from core-lib
type Orientation = 'portrait' | 'landscape';

@Component({
  selector: 'app-farmer-insights',
  templateUrl: './farmer-insights.component.html',
  styleUrls: ['./farmer-insights.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    MatButton,
    MatIcon,
    PrintPageComponent,
    DataFilterComponent,
    DataViewComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FarmerInsightsComponent implements OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly datePipe = inject(AgrDatePipe);
  private readonly dialog = inject(DialogService);
  private readonly brandingService = inject(BrandingService);

  pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  customer$: Observable<Customer> = this.store.pipe(select(UserSelectors.selectCustomer));
  queryableFlockData$: Observable<QueryableFlockData> = this.store.select(getQueryableFlockData);
  benchmarkSettings$: Observable<BenchmarkSettings> = this.store.select(getBenchmarkSettings);
  selectedViewMode$: Observable<ViewMode> = this.store.select(getSelectedViewMode);
  isDetailedView$: Observable<boolean> = this.store.select(getIsDetailedModeSelected);
  printOrientation: Orientation = 'portrait';
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);

  customer: Customer;
  isDataEmpty: boolean;
  location: SelectedLocation;

  showAgrifirmLogo = true;
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  private readonly customerSubscription: Subscription;
  private readonly isDataEmptySubscription: Subscription;
  private readonly viewModeSubscription: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.customerSubscription = this.customer$.subscribe((c) => (this.customer = c));
    this.isDataEmptySubscription = this.queryableFlockData$.subscribe(
      (c) => (this.isDataEmpty = c === null || c.locations?.length === 0),
    );

    this.location$.subscribe((location) => {
      this.location = location;
    });

    this.viewModeSubscription = combineLatest([
      this.selectedViewMode$,
      this.isDetailedView$,
    ]).subscribe(([viewMode, isDetailedView]) => {
      if (viewMode === ViewMode.Table && isDetailedView) {
        this.printOrientation = 'landscape';
      } else {
        this.printOrientation = 'portrait';
      }
    });

    if (this.brandingService.isTenant('Agravis')) {
      this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
      this.showAgrifirmLogo = false;
    }
  }

  ngOnDestroy(): void {
    if (this.customerSubscription) {
      this.customerSubscription.unsubscribe();
    }
    if (this.isDataEmptySubscription) {
      this.isDataEmptySubscription.unsubscribe();
    }
    if (this.viewModeSubscription) {
      this.viewModeSubscription.unsubscribe();
    }
  }

  print() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT ${this.localeService.translate('app.farmer-insights')} - ${
      this.customer?.name
    } - ${this.datePipe.transform(String(new Date()))}`;
    if (globalThis) {
      document.title = proposedTitle;
      globalThis.print();
      document.title = existingTitle;
    }
  }

  openSettings() {
    this.benchmarkSettings$.pipe(take(1)).subscribe((settings) => {
      this.dialog
        .form(BenchmarkSettingsComponent, {
          setPendingChanges: false,
          settings,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              setFarmerInsightsBenchmarkSettings({ settings: result.result.settings, save: true }),
            );
          }
        });
    });
  }
}
